import "./style.css";
import curDot from "cursor-dot";
import * as THREE from "three";
import * as dat from "lil-gui";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import gsap from "gsap";

const cursorDot = curDot({
  easing: 4,
  borderColor: "transparent",
});

cursorDot.over("section", {
  scale: 1.5,
  borderColor: "transparent",
  background: "#fff",
});

// Base
const parameters = {
  frontLightColor: "0xffecdb",
  backLightColor: "0xffffff",
};

// const gui = new dat.GUI();
// gui.addColor(parameters, "frontLightColor");
// gui.addColor(parameters, "backLightColor");

const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

const canvas = document.querySelector("canvas.threeJS");
const scene = new THREE.Scene();
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
  alpha: true,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

// lights
const frontLight = new THREE.PointLight(parameters.frontLightColor, 3);
const backLight = new THREE.PointLight(parameters.backLightColor, 2);
frontLight.castShadow = true;
frontLight.shadow.mapSize.width = 1024;
frontLight.shadow.mapSize.height = 1024;
backLight.castShadow = true;
backLight.shadow.mapSize.width = 1024;
backLight.shadow.mapSize.height = 1024;
frontLight.position.set(10, 10, 10);
backLight.position.set(-10, -10, 10);
scene.add(frontLight);
scene.add(backLight);

// gui.onChange(() => {
//   frontLight.color.set(parameters.frontLightColor);
//   backLight.color.set(parameters.backLightColor);
// });

const particlesGeometry = new THREE.DodecahedronGeometry(-6, 100);
const particlesMaterial = new THREE.PointsMaterial({
  size: 0.02,
  sizeAttenuation: true,
});

const particles = new THREE.Points(particlesGeometry, particlesMaterial);
scene.add(particles);

const meshesDistance = 4;
let sectionMeshes = [];

// Models
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("/models/draco/gltf/");
const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(dracoLoader);

gltfLoader.load("/models/frappe.gltf", (gltf) => {
  const meshes = gltf.scene.children[0];
  meshes.scale.set(4, 4, 4);
  meshes.rotation.set(0.5, 0.1, 0);
  meshes.position.x = 0.5;
  meshes.position.y = -meshesDistance * 0;
  sectionMeshes = [...sectionMeshes, meshes];
  scene.add(meshes);
});
gltfLoader.load("/models/cocktail.gltf", (gltf) => {
  const meshes = gltf.scene.children[0];
  meshes.scale.set(4, 4, 4);
  meshes.position.x = -2;
  meshes.position.y = -meshesDistance * 1;
  meshes.rotation.set(0.5, 0.1, 0);
  sectionMeshes = [...sectionMeshes, meshes];
  scene.add(meshes);
});
gltfLoader.load("/models/bowlBroth.gltf", (gltf) => {
  const meshes = gltf.scene.children[0];
  meshes.scale.set(3, 3, 3);
  meshes.rotation.set(0.5, 0.1, 0);
  meshes.position.x = 0.5;
  meshes.position.y = -meshesDistance * 2;
  sectionMeshes = [...sectionMeshes, meshes];
  scene.add(meshes);
});

// Scroll
let scrollY = window.scrollY;
let currentSection = 0;
window.addEventListener("scroll", () => {
  scrollY = window.scrollY;
  const newSection = Math.round(scrollY / sizes.height);
  if (sectionMeshes.length === 3 && newSection !== currentSection) {
    currentSection = newSection;
    gsap.to(sectionMeshes[currentSection].rotation, {
      duration: 1.5,
      ease: "power2.inOut",
      x: "+=6",
      y: "+=3",
    });
  }
});

// cursor
const cursor = {};
cursor.x = 0;
cursor.y = 0;
window.addEventListener("mousemove", (event) => {
  cursor.x = event.clientX / sizes.width - 0.5;
  cursor.y = event.clientY / sizes.height - 0.5;
});

// Update size, camera, renderer when resizing the window
window.addEventListener("resize", () => {
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;

  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

// Base camera
const cameraGroup = new THREE.Group();
scene.add(cameraGroup);
const camera = new THREE.PerspectiveCamera(
  50,
  sizes.width / sizes.height,
  0.1,
  100
);
camera.position.x = -1;
camera.position.z = 4;
camera.position.y = 0.5;
cameraGroup.add(camera);

// Animate
const clock = new THREE.Clock();
let previousTime = 0;

const tick = () => {
  const elapsedTime = clock.getElapsedTime();
  const deltaTime = elapsedTime - previousTime;
  previousTime = elapsedTime;

  if (sectionMeshes.length === 3) {
    for (const mesh of sectionMeshes) {
      mesh.rotation.y += deltaTime * 0.25;
    }
  }

  camera.position.y = (-scrollY / sizes.height) * meshesDistance + 1;

  // Animate camera by mouse
  const parallaxX = cursor.x;
  const parallaxY = -cursor.y;
  cameraGroup.position.x +=
    (parallaxX - cameraGroup.position.x) * 5 * deltaTime;
  cameraGroup.position.y +=
    (parallaxY - cameraGroup.position.y) * 5 * deltaTime;

  // Render
  renderer.render(scene, camera);

  // Call tick again on the next frame
  window.requestAnimationFrame(tick);
};

tick();
